import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PrimengImports } from '../../../shared/primeng-imports';
import { TooltipModule } from 'primeng/tooltip';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { WalletService } from '../../../service/wallet.service';
import { UserService } from '../../../service/user.service';
import { TokenIdService } from '../../../service/token-id.service';
import { FormsModule } from '@angular/forms';
import { WithdrawalService } from '../../../service/withdrawal.service';
import { MessageService } from 'primeng/api';

type WalletSummary = {
  balance: number;
  roiAvailable: number;
  currency: string;
  isLocked: boolean;
  totalDeposits: number;
  totalWithdrawals: number;
  pendingWithdrawals: number;
  spendableBalance: number;
};

type PortfolioRow = {
  id: string;
  name: string;
  roiAvailableFromThisPortfolio: number;
  roiEarnedLifetime: number;
};

@Component({
  selector: 'app-investor-wallet-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, PrimengImports, TooltipModule,  LucideIconsModule],
  templateUrl: './investor-wallet-tab.component.html',
  styleUrls: ['./investor-wallet-tab.component.css'],
  providers: [MessageService]
})
export class InvestorWalletTabComponent implements OnInit {
  // ui state
  loadingSummary = true;
  loadingPortfolios = true;
  loadingActivity = true;
  loadingWithdrawals = true;
  errorMsg = '';
  historyHighlight = false;
  sortOptions = [

            { label: 'ROI: High → Low', value: 'roiDesc' },
            { label: 'ROI: Low → High', value: 'roiAsc' },
            { label: 'Name: A → Z', value: 'nameAsc' },
            { label: 'Name: Z → A', value: 'nameDesc' }
          ];

  // data
  summary: WalletSummary | null = null;
  portfolios: any;
  filtered: any[] = [];
  recentActivity: any[] = [];
  withdrawalHistory: any[] = [];

  // chart (portfolio diversification)
  chartData: any;
  chartOptions: any;

  // withdraw dialog
  withdrawDialogVisible = false;
  withdrawAmount: number | null = null;
  withdrawNotes: string = '';
  submittingWithdraw = false;

  // filters
  search = '';
  sortKey: 'roiDesc' | 'roiAsc' | 'nameAsc' | 'nameDesc' = 'roiDesc';

  // derived
  otherFunds = computed(() => {
    if (!this.summary) return 0;
    return Math.max(0, Number(this.summary.balance || 0) - Number(this.summary.roiAvailable || 0));
  });

  roiSharePct = computed(() => {
    if (!this.summary || !this.summary.balance) return 0;
    const pct = (this.summary.roiAvailable / this.summary.balance) * 100;
    return Math.max(0, Math.min(100, pct));
  });

  constructor(
    private walletSvc: WalletService,
    private userSvc: UserService,
    private tokenSvc: TokenIdService,
    private withdrawalSvc: WithdrawalService,
    private messageSvc: MessageService
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll() {
    this.errorMsg = '';
    const token = this.userSvc.getAuthToken();
    const userId = this.tokenSvc.getUserIdFromToken(token || '');
    if (!userId) {
      this.errorMsg = 'Unable to resolve user. Please sign in again.';
      this.loadingSummary = this.loadingPortfolios = this.loadingActivity = false;
      return;
    }

    // summary
    this.loadingSummary = true;
    this.walletSvc.getWalletBalance(userId).subscribe({
      next: (response: any) => {
        const data = response?.data || response; // Handle both direct data and wrapped response
        this.summary = {
          balance: Number(data?.balance || 0),
          roiAvailable: Number(data?.roiAvailable || 0),
          currency: data?.currency || 'AED',
          isLocked: !!data?.isLocked,
          totalDeposits: Number(data?.totalDeposits || 0),
          totalWithdrawals: Number(data?.totalWithdrawals || 0),
          pendingWithdrawals: Number(data?.pendingWithdrawals || 0),
          spendableBalance: Number(data?.spendableBalance || 0)
        };
        this.loadingSummary = false;
        // Summary loaded; chart will be built from portfolio data when it arrives
      },
      error: (err) => {
        this.errorMsg = err?.message || 'Failed to load wallet balance.';
        this.loadingSummary = false;
      }
    });

    // portfolios w/ ROI
    this.loadingPortfolios = true;
    this.walletSvc.getInvestmentsForRoi(userId).subscribe({
      next: (rows:any) => {
        // this.portfolios = rows.map((r: any) => ({
        //   id: r?._id || r?.id,
        //   name: r?.portfolioId?.name || '—',
        //   roiAvailableFromThisPortfolio: Number(r?.roiAvailableFromThisPortfolio || 0),
        //   roiEarnedLifetime: Number(r?.roiEarnedLifetime ?? r?.roiEarnedTotal ?? 0)
        // }));
        this.portfolios = rows.data.items;
        console.log('portfolios', this.portfolios);


        this.applyFilters();
        this.buildDiversificationChart();
        this.loadingPortfolios = false;
      },
      error: (err) => {
        this.errorMsg = err?.message || 'Failed to load portfolio ROI.';
        this.loadingPortfolios = false;
      }
    });

    // recent wallet activity (ROI credits first)
    this.loadingActivity = true;
    this.walletSvc.listRecentWalletTransactions(50).subscribe({
      next: (list) => {
        // Credit history only and include portfolio details if available
        const creditTypes = new Set(['Topup', 'Refund', 'ROI_CREDIT']);
        this.recentActivity = (list || []).filter((t: any) => creditTypes.has(t?.transactionType));
        this.loadingActivity = false;
      },
      error: () => {
        this.recentActivity = [];
        this.loadingActivity = false;
      }
    });

    // withdrawal history
    this.loadingWithdrawals = true;
    this.withdrawalSvc.getWithdrawalsHistory(1, 20).subscribe({
      next: (response) => {
        this.withdrawalHistory = response?.data || [];
        this.loadingWithdrawals = false;
      },
      error: () => {
        this.withdrawalHistory = [];
        this.loadingWithdrawals = false;
      }
    });
  }

  // filters/sort
  applyFilters() {
    const q = (this.search || '').toLowerCase().trim();
    let arr = [...this.portfolios];

    if (q) {
      arr = arr.filter(p => p.name.toLowerCase().includes(q));
    }

    switch (this.sortKey) {
      case 'roiDesc':
        arr.sort((a, b) => b.roiAvailableFromThisPortfolio - a.roiAvailableFromThisPortfolio);
        break;
      case 'roiAsc':
        arr.sort((a, b) => a.roiAvailableFromThisPortfolio - b.roiAvailableFromThisPortfolio);
        break;
      case 'nameAsc':
        arr.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'nameDesc':
        arr.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }

    this.filtered = arr;
  }

  onWithdraw() {
    this.withdrawDialogVisible = true;
  }

  scrollToHistory() {
    const el = document.getElementById('withdrawal-history-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // brief highlight to draw attention
      this.historyHighlight = true;
      setTimeout(() => (this.historyHighlight = false), 1500);
    }
  }

  onAddMoney() {
    // TODO: hook your top-up flow
    console.log('Add Money clicked');
  }

  fmtMoney(n: number) {
    const cur = this.summary?.currency || 'AED';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(Number(n || 0));
  }

  fmtDate(d: string) {
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  get canWithdraw() {
    return !!this.summary && !this.summary.isLocked && this.summary.spendableBalance > 0;
  }

  get withdrawDisabledReason(): string {
    if (!this.summary) return '';
    if (this.summary.isLocked) return 'Wallet is locked by admin';
    if (this.summary.spendableBalance === 0 && this.summary.pendingWithdrawals > 0) 
      return 'All funds are currently on hold.';
    if (this.summary.spendableBalance === 0 && this.summary.pendingWithdrawals === 0) 
      return 'No funds available to withdraw.';
    return '';
  }

  private buildDiversificationChart() {
    const items = Array.isArray(this.portfolios) ? this.portfolios : [];
    const labels: string[] = items.map((p: any) => p?.portfolioId?.name || p?.name || '—');
    const data: number[] = items.map((p: any) => Number(p?.roiAvailable || p?.roiAvailableFromThisPortfolio || 0));

    // Fallback if nothing to show
    const hasData = data.some(v => v > 0);
    if (!hasData) {
      this.chartData = { labels: ['No Data'], datasets: [{ data: [1], backgroundColor: ['#e5e7eb'], borderColor: '#ffffff', borderWidth: 2 }] };
      this.chartOptions = { plugins: { legend: { display: false } } };
      return;
    }

    // Color palette
    const palette = ['#0ea5e9','#22c55e','#eab308','#ef4444','#8b5cf6','#14b8a6','#f97316','#a3e635','#06b6d4','#f43f5e','#84cc16','#6366f1'];
    const colors = data.map((_, i) => palette[i % palette.length]);
    const hover = colors.map(c => c);

    this.chartData = {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          hoverBackgroundColor: hover,
          borderColor: '#ffffff',
          borderWidth: 2
        }
      ]
    };
    this.chartOptions = {
      cutout: '60%',
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { usePointStyle: true }
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.parsed || 0;
              return `${ctx.label}: ${this.fmtMoney(val)}`;
            }
          }
        }
      }
    };
  }

  submitWithdraw() {
    if (!this.withdrawAmount || this.withdrawAmount <= 0) {
      this.messageSvc.add({ severity: 'warn', summary: 'Invalid amount', detail: 'Enter a valid amount.' });
      return;
    }
    if (this.withdrawAmount > (this.summary?.spendableBalance || 0)) {
      this.messageSvc.add({ severity: 'warn', summary: 'Insufficient funds', detail: 'Amount exceeds available balance.' });
      return;
    }
    if (!this.canWithdraw) {
      this.messageSvc.add({ severity: 'warn', summary: 'Unavailable', detail: this.withdrawDisabledReason });
      return;
    }
    
    this.submittingWithdraw = true;
    const payload = {
      amount: this.withdrawAmount,
      userNotes: this.withdrawNotes || undefined
    };

    // Optimistic update
    const originalSpendable = this.summary?.spendableBalance || 0;
    const originalPending = this.summary?.pendingWithdrawals || 0;
    
    this.withdrawalSvc.createRequest(payload).subscribe({
      next: () => {
        // Update UI optimistically
        if (this.summary) {
          this.summary.spendableBalance = originalSpendable - this.withdrawAmount!;
          this.summary.pendingWithdrawals = originalPending + this.withdrawAmount!;
        }
        
        this.messageSvc.add({ 
          severity: 'success', 
          summary: 'Request submitted', 
          detail: 'Your withdrawal request has been submitted successfully.' 
        });
        this.withdrawDialogVisible = false;
        this.withdrawAmount = null;
        this.withdrawNotes = '';
        this.submittingWithdraw = false;
        
        // Refresh data to ensure consistency
        this.loadAll();
      },
      error: (err) => {
        const errorMsg = err?.message || 'Could not create request.';
        if (errorMsg.includes('balance changed') || errorMsg.includes('race condition')) {
          this.messageSvc.add({ 
            severity: 'warn', 
            summary: 'Balance changed', 
            detail: 'Balance changed—please try again.' 
          });
          this.loadAll(); // Refresh balance
        } else {
          this.messageSvc.add({ severity: 'error', summary: 'Failed', detail: errorMsg });
        }
        this.submittingWithdraw = false;
      }
    });
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    switch (status?.toLowerCase()) {
      case 'requested': return 'secondary';
      case 'initiated': return 'info';
      case 'processing': return 'warning';
      case 'completed': return 'success';
      case 'rejected':
      case 'canceled': return 'danger';
      default: return 'secondary';
    }
  }

  getTransactionSeverity(type: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    switch (type?.toLowerCase()) {
      case 'roi_credit': return 'success';
      case 'topup': return 'info';
      case 'refund': return 'warning';
      case 'debit': return 'danger';
      default: return 'secondary';
    }
  }
}
